import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, MapPin, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Utilisation de Formspree pour envoyer les données du formulaire par email
      const response = await fetch("https://formspree.io/f/xrbpoweq", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          type: "Message du formulaire de contact"
        }),
      });

      if (response.ok) {
        toast({
          title: "Message envoyé",
          description: "Nous vous contacterons dans les plus brefs délais.",
          duration: 5000,
        });
        // Réinitialisation du formulaire
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
      } else {
        throw new Error("Erreur lors de l'envoi du formulaire");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du formulaire. Veuillez réessayer.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-section" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6">
        <h2 className="section-title text-center mb-4">Contact</h2>
        <p className="text-center text-patrimoine-muted max-w-2xl mx-auto mb-12">
          Prendre contact avec notre équipe pour discuter de vos besoins en gestion patrimoniale.
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-2xl font-serif font-semibold mb-6 text-patrimoine-DEFAULT">Envoyez-nous un message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Prénom</Label>
                  <Input 
                    id="firstName" 
                    placeholder="Votre prénom" 
                    required 
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nom</Label>
                  <Input 
                    id="lastName" 
                    placeholder="Votre nom" 
                    required 
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="votre@email.com" 
                  required 
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea 
                  id="message" 
                  placeholder="Comment pouvons-nous vous aider ?" 
                  className="min-h-[120px]"
                  required
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>
              
              <Button 
                type="submit" 
                className="cta-button w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
              </Button>
            </form>
          </div>
          
          {/* Contact Information and Map */}
          <div>
            <h3 className="text-2xl font-serif font-semibold mb-6 text-patrimoine-DEFAULT">Nos Coordonnées</h3>
            
            <div className="space-y-8 mb-10">
              <div className="flex">
                <MapPin className="h-6 w-6 mr-4 text-patrimoine-gold flex-shrink-0" />
                <div>
                  <h4 className="font-serif font-medium text-lg mb-1">Adresse</h4>
                  <p className="text-patrimoine-muted">47 Boulevard de Beauséjour, 75016 Paris, France</p>
                </div>
              </div>
              
              <div className="flex">
                <Mail className="h-6 w-6 mr-4 text-patrimoine-gold flex-shrink-0" />
                <div>
                  <h4 className="font-serif font-medium text-lg mb-1">Email</h4>
                  <p className="text-patrimoine-muted">contact@la-patrimoniale-gestion.com</p>
                </div>
              </div>
              
              <div className="flex">
                <Clock className="h-6 w-6 mr-4 text-patrimoine-gold flex-shrink-0" />
                <div>
                  <h4 className="font-serif font-medium text-lg mb-1">Horaires d'ouverture</h4>
                  <p className="text-patrimoine-muted">Lundi - Jeudi: 9h00 - 19h00</p>
                  <p className="text-patrimoine-muted">Vendredi: 9h00 - 13h00</p>
                  <p className="text-patrimoine-muted">Samedi: Fermé</p>
                  <p className="text-patrimoine-muted">Dimanche: Fermé</p>
                </div>
              </div>
            </div>
            
            {/* Updated Google Maps Embed */}
            <div className="w-full h-[300px] rounded-lg overflow-hidden border border-gray-200 shadow-sm">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5250.146361210706!2d2.2679899!3d48.856814899999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67aad2346b9dd%3A0xe879b50c0133586f!2s47%20Bd%20de%20Beaus%C3%A9jour%2C%2075016%20Paris!5e0!3m2!1sfr!2sfr!4v1744974291038!5m2!1sfr!2sfr" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Notre adresse"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(ContactSection);
